import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Confetti } from "@/components/tools/Confetti";
import {
  CopyButton,
  BrandedExportActions,
  EmptyHint,
  FullscreenButton,
  HistoryPanel,
  ListInput,
  Panel,
  ResetButton,
  SoundToggle,
  usePersistentState,
  useSfx,
} from "@/components/tools/ToolKit";
import { DEFAULT_CLASS_LIST, dedupe, parseList, randomInt, shuffle } from "@/lib/random-engine";
import {
  Disc3,
  Shuffle,
  ArrowUpDown,
  RotateCcw,
  Trophy,
  Trash2,
  Copy,
  Type,
  Hash,
  Image as ImageIcon,
  Upload,
  Plus,
  X,
  Share2,
  Code,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import { copyText } from "@/lib/random-engine";

export type WheelMode = "text" | "numbers" | "images";

export interface WheelItem {
  id: string;
  label: string;
  imageUrl?: string;
}

// Preset Emoji Image Items
const PRESET_EMOJI_ITEMS: WheelItem[] = [
  { id: "e1", label: "Gold Crown", imageUrl: "https://images.unsplash.com/photo-1579783902614-a3fb3927b675?w=150&auto=format&fit=crop&q=80" },
  { id: "e2", label: "Trophy", imageUrl: "https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?w=150&auto=format&fit=crop&q=80" },
  { id: "e3", label: "Star", imageUrl: "https://images.unsplash.com/photo-1534447677768-be436bb09401?w=150&auto=format&fit=crop&q=80" },
  { id: "e4", label: "Target", imageUrl: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=150&auto=format&fit=crop&q=80" },
  { id: "e5", label: "Rocket", imageUrl: "https://images.unsplash.com/photo-1517976487492-5750f3195933?w=150&auto=format&fit=crop&q=80" },
  { id: "e6", label: "Diamond", imageUrl: "https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=150&auto=format&fit=crop&q=80" },
];

// Vibrant multi-color wheel slice palette
const SLICE_COLORS = [
  "oklch(0.58 0.22 250)", // Vibrant Royal Blue
  "oklch(0.65 0.22 150)", // Emerald Green
  "oklch(0.78 0.20 80)",  // Sunshine Gold
  "oklch(0.64 0.24 30)",  // Bright Coral Orange
  "oklch(0.58 0.25 300)", // Deep Electric Purple
  "oklch(0.68 0.20 195)", // Cyan Turquoise
  "oklch(0.62 0.26 350)", // Neon Rose Pink
  "oklch(0.52 0.24 275)", // Rich Sapphire Indigo
];

const DURATIONS = [3, 5, 8, 10];

function polar(cx: number, cy: number, r: number, angleDeg: number) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)] as const;
}

/** Smooth quartic ease-out deceleration curve */
function easeOutQuart(t: number) {
  return 1 - Math.pow(1 - t, 4);
}

export function SpinWheel({
  storageKey = "pickamini:wheel",
  defaultItems,
  defaultMode = "text",
  entriesLabel = "Entries",
}: {
  storageKey?: string;
  defaultItems?: string[];
  defaultMode?: WheelMode;
  entriesLabel?: string;
} = {}) {
  // Wheel Mode: "text" | "numbers" | "images"
  const [mode, setMode] = useState<WheelMode>(defaultMode);

  // Mode 1: Text Items
  const [rawText, setRawText] = usePersistentState(
    storageKey,
    (defaultItems ?? DEFAULT_CLASS_LIST.slice(0, 8)).join("\n"),
  );

  // Mode 2: Number Range State
  const [numMin, setNumMin] = useState(1);
  const [numMax, setNumMax] = useState(10);

  // Mode 3: Image Items State
  const [imageItems, setImageItems] = useState<WheelItem[]>(PRESET_EMOJI_ITEMS);

  // General Wheel Controls
  const [soundOn, setSoundOn] = usePersistentState(`${storageKey}:sound`, true);
  const [duration, setDuration] = useState(5);
  const [removeWinner, setRemoveWinner] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  
  const [winnerItem, setWinnerItem] = useState<WheelItem | null>(null);
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const [confetti, setConfetti] = useState(false);
  const [knock, setKnock] = useState(0);

  const stageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);
  const sfx = useSfx(soundOn);

  // Read shareable URL parameters (?items=Item1,Item2 or ?entries=...)
  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const itemsParam = params.get("items") || params.get("entries");
      if (itemsParam) {
        const decoded = itemsParam
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        if (decoded.length >= 2) {
          setRawText(decoded.join("\n"));
          toast.success(`Loaded ${decoded.length} items from shared link!`);
        }
      }
    }
  }, []);

  useEffect(
    () => () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  // Compute Active Items List Based on Mode
  const getActiveItems = (): WheelItem[] => {
    if (mode === "text") {
      const parsed = dedupe(parseList(rawText));
      return parsed.map((name, i) => ({ id: `txt-${i}`, label: name }));
    }
    if (mode === "numbers") {
      const min = Math.min(numMin, numMax);
      const max = Math.max(numMin, numMax);
      const count = Math.min(100, Math.max(2, max - min + 1));
      return Array.from({ length: count }, (_, i) => ({
        id: `num-${min + i}`,
        label: String(min + i),
      }));
    }
    // images mode
    return imageItems;
  };

  const items = getActiveItems();
  const sliceAngle = items.length ? 360 / items.length : 360;

  const spin = () => {
    if (spinning || items.length < 2) return;

    const winnerIndex = randomInt(items.length);
    const chosen = items[winnerIndex]!;
    const targetAngle = winnerIndex * sliceAngle + sliceAngle / 2;
    const fromAngle = rotation;
    const fullSpins = 360 * (5 + Math.floor(duration / 2));
    const toAngle = fromAngle + fullSpins + ((360 - targetAngle - (fromAngle % 360)) % 360);

    setSpinning(true);
    setWinnerItem(null);
    setShowWinnerModal(false);
    setConfetti(false);
    sfx.whoosh();

    const startedAt = performance.now();
    const totalMs = duration * 1000;
    let lastSegment = -1;

    const step = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / totalMs);
      const currentAngle = fromAngle + (toAngle - fromAngle) * easeOutQuart(progress);
      setRotation(currentAngle);

      const segment = Math.floor((((360 - (currentAngle % 360)) % 360) / sliceAngle) % items.length);
      if (segment !== lastSegment) {
        if (lastSegment !== -1) {
          sfx.tick(Math.max(0.15, 1 - progress));
          setKnock((k) => k + 1);
        }
        lastSegment = segment;
      }

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
        return;
      }

      setRotation(toAngle);
      setWinnerItem(chosen);
      setSpinning(false);
      setShowWinnerModal(true);
      setHistory((h) => [chosen.label, ...h].slice(0, 50));
      setConfetti(true);
      sfx.fanfare();

      window.setTimeout(() => setConfetti(false), 2400);

      if (removeWinner) {
        if (mode === "text") {
          setRawText(items.filter((item) => item.label !== chosen.label).map((i) => i.label).join("\n"));
        } else if (mode === "images") {
          setImageItems((prev) => prev.filter((item) => item.id !== chosen.id));
        }
      }
    };

    frameRef.current = requestAnimationFrame(step);
  };

  const removeCurrentWinner = () => {
    if (!winnerItem) return;
    if (mode === "text") {
      setRawText(items.filter((item) => item.label !== winnerItem.label).map((i) => i.label).join("\n"));
    } else if (mode === "images") {
      setImageItems((prev) => prev.filter((item) => item.id !== winnerItem.id));
    }
    toast.success(`Removed "${winnerItem.label}"`);
    setShowWinnerModal(false);
  };

  const shuffleEntries = () => {
    sfx.click();
    if (mode === "text") {
      const parsed = dedupe(parseList(rawText));
      setRawText(shuffle(parsed).join("\n"));
    } else if (mode === "images") {
      setImageItems(shuffle([...imageItems]));
    }
  };

  const sortEntries = () => {
    sfx.click();
    if (mode === "text") {
      const parsed = dedupe(parseList(rawText));
      setRawText([...parsed].sort().join("\n"));
    }
  };

  // Handle Image Upload for Image Mode
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const url = event.target?.result as string;
        if (url) {
          setImageItems((prev) => [
            ...prev,
            {
              id: `img-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
              label: file.name.replace(/\.[^/.]+$/, ""),
              imageUrl: url,
            },
          ]);
        }
      };
      reader.readAsDataURL(file);
    });
    toast.success(`Added ${files.length} image(s) to wheel`);
    e.target.value = "";
  };

  const handleShareWheel = async () => {
    if (typeof window === "undefined") return;
    const currentItems = getActiveItems().map((i) => i.label);
    const url = new URL(window.location.href);
    url.searchParams.set("items", currentItems.join(","));
    await copyText(url.toString());
    toast.success("Shareable wheel link copied to clipboard!");
  };

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-[340px_1fr_320px] xl:grid-cols-[360px_1fr_340px]">
        {/* Settings & Mode Selector Panel */}
        <Panel title="Wheel Mode & Setup" className="no-print space-y-4">
          {/* Mode Switcher Tabs */}
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Wheel Type
            </p>
            <div className="grid grid-cols-3 gap-1 rounded-xl bg-secondary p-1">
              <button
                type="button"
                onClick={() => {
                  setMode("text");
                  sfx.click();
                }}
                className={`flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-bold transition-all ${
                  mode === "text"
                    ? "bg-card text-primary shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Type className="size-3.5" /> Text
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode("numbers");
                  sfx.click();
                }}
                className={`flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-bold transition-all ${
                  mode === "numbers"
                    ? "bg-card text-primary shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Hash className="size-3.5" /> Numbers
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode("images");
                  sfx.click();
                }}
                className={`flex items-center justify-center gap-1 py-2 rounded-lg text-xs font-bold transition-all ${
                  mode === "images"
                    ? "bg-card text-primary shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <ImageIcon className="size-3.5" /> Images
              </button>
            </div>
          </div>

          {/* MODE 1: Text / Names Input */}
          {mode === "text" && (
            <div>
              <ListInput value={rawText} onChange={setRawText} label={entriesLabel} count={items.length} />
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Button type="button" variant="outline" size="sm" onClick={shuffleEntries} disabled={items.length < 2}>
                  <Shuffle className="size-3.5 mr-1" /> Shuffle
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={sortEntries} disabled={items.length < 2}>
                  <ArrowUpDown className="size-3.5 mr-1" /> Sort A-Z
                </Button>
                {defaultItems && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setRawText(defaultItems.join("\n"));
                      sfx.click();
                      toast.success("Reset entries to defaults");
                    }}
                  >
                    <RotateCcw className="size-3.5 mr-1" /> Defaults
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* MODE 2: Numbers Range Controls */}
          {mode === "numbers" && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs font-medium">
                  Min Number
                  <Input
                    type="number"
                    value={numMin}
                    onChange={(e) => setNumMin(Number(e.target.value))}
                    className="mt-1 font-bold"
                  />
                </label>
                <label className="text-xs font-medium">
                  Max Number
                  <Input
                    type="number"
                    value={numMax}
                    onChange={(e) => setNumMax(Number(e.target.value))}
                    className="mt-1 font-bold"
                  />
                </label>
              </div>

              <div>
                <p className="mb-1.5 text-xs font-semibold text-muted-foreground">Quick Number Range</p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { label: "1–6 (Die)", min: 1, max: 6 },
                    { label: "1–10", min: 1, max: 10 },
                    { label: "1–20", min: 1, max: 20 },
                    { label: "1–50", min: 1, max: 50 },
                  ].map((p) => (
                    <Button
                      key={p.label}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => {
                        setNumMin(p.min);
                        setNumMax(p.max);
                        sfx.click();
                      }}
                    >
                      {p.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* MODE 3: Image / Photo Upload Manager */}
          {mode === "images" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{imageItems.length} Images</span>
                <label className="inline-flex cursor-pointer items-center rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground shadow-sm hover:bg-primary/90 transition-all">
                  <Upload className="size-3.5 mr-1.5" /> Upload Photos
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </label>
              </div>

              {/* Image Grid Preview List */}
              <div className="max-h-48 overflow-y-auto grid grid-cols-3 gap-2 p-2 border rounded-xl bg-secondary/30">
                {imageItems.map((img) => (
                  <div key={img.id} className="relative group rounded-lg overflow-hidden border border-border bg-card aspect-square">
                    <img src={img.imageUrl} alt={img.label} className="size-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setImageItems((prev) => prev.filter((i) => i.id !== img.id))}
                      className="absolute top-1 right-1 flex size-5 items-center justify-center rounded-full bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove image"
                    >
                      <X className="size-3" />
                    </button>
                    <span className="absolute bottom-0 inset-x-0 bg-black/60 text-[9px] text-white text-center truncate px-1 py-0.5">
                      {img.label}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setImageItems(PRESET_EMOJI_ITEMS)}
                  className="text-xs"
                >
                  Reset Presets
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setImageItems([])}
                  className="text-xs text-destructive"
                >
                  Clear Images
                </Button>
              </div>
            </div>
          )}

          {/* Spin Duration & Options */}
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Spin Duration
            </p>
            <div className="flex gap-1.5">
              {DURATIONS.map((d) => (
                <Button
                  key={d}
                  type="button"
                  size="sm"
                  variant={duration === d ? "default" : "outline"}
                  onClick={() => {
                    setDuration(d);
                    sfx.click();
                  }}
                >
                  {d}s
                </Button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-xl border bg-secondary/50 px-3.5 py-2.5">
            <span className="text-xs font-medium">Remove winner after spin</span>
            <Switch checked={removeWinner} onCheckedChange={setRemoveWinner} />
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <SoundToggle on={soundOn} onToggle={() => setSoundOn(!soundOn)} />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-xs font-semibold"
              onClick={handleShareWheel}
            >
              <Share2 className="size-3.5 mr-1 text-primary" /> Share Wheel
            </Button>
            <ResetButton
              onClick={() => {
                setWinnerItem(null);
                setRotation(0);
                setHistory([]);
              }}
            />
          </div>
        </Panel>

        {/* Wheel Stage */}
        <div
          ref={stageRef}
          className="surface-card relative flex flex-col items-center justify-between gap-6 p-6 min-h-[460px] sm:min-h-[540px] lg:min-h-[600px] xl:min-h-[660px]"
        >
          <Confetti active={confetti} />
          <div className="w-full flex items-center justify-between no-print">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Disc3 className="size-4 text-primary" />
              <span className="capitalize">{mode} Mode ({items.length} {items.length === 1 ? "Item" : "Items"})</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
                onClick={handleShareWheel}
                title="Copy shareable link"
              >
                <Share2 className="size-3.5 mr-1 text-primary" /> Share
              </Button>
              <FullscreenButton targetRef={stageRef} />
            </div>
          </div>

          {/* SVG Wheel Display */}
          <div className="relative aspect-square w-full max-w-[440px] sm:max-w-[500px] lg:max-w-[560px] xl:max-w-[620px] my-auto">
            {/* Top Pointer Peg */}
            <div className="absolute left-1/2 -top-3 z-20 -translate-x-1/2 drop-shadow-md">
              <div
                key={knock}
                className={`size-0 origin-top border-x-[14px] border-t-[26px] border-x-transparent border-t-amber-500 ${
                  spinning ? "animate-knock" : ""
                }`}
              />
            </div>

            {/* Wheel Container */}
            <div className="relative size-full rounded-full border-4 border-card shadow-lift overflow-hidden">
              <svg
                viewBox="0 0 200 200"
                className="size-full"
                style={{ transform: `rotate(${rotation}deg)`, willChange: "transform" }}
                role="img"
                aria-label="Spinning wheel"
              >
                <defs>
                  {items.map((item, idx) => {
                    if (!item.imageUrl) return null;
                    const cAngle = idx * sliceAngle + sliceAngle / 2;
                    return (
                      <clipPath key={`clip-${item.id}-${idx}`} id={`clip-${item.id}-${idx}`}>
                        <circle cx="176" cy="100" r="8.5" transform={`rotate(${cAngle - 90} 100 100)`} />
                      </clipPath>
                    );
                  })}
                </defs>

                {items.length < 2 ? (
                  <circle cx="100" cy="100" r="98" fill="oklch(0.95 0.01 220)" />
                ) : (
                  items.map((item, i) => {
                    const startA = i * sliceAngle;
                    const endA = startA + sliceAngle;
                    const [x1, y1] = polar(100, 100, 98, startA);
                    const [x2, y2] = polar(100, 100, 98, endA);
                    const largeArc = sliceAngle > 180 ? 1 : 0;
                    const centerAngle = startA + sliceAngle / 2;

                    return (
                      <g key={`${item.id}-${i}`}>
                        <path
                          d={`M100 100 L ${x1} ${y1} A 98 98 0 ${largeArc} 1 ${x2} ${y2} Z`}
                          fill={SLICE_COLORS[i % SLICE_COLORS.length]}
                          stroke="white"
                          strokeWidth="1.2"
                        />

                        {/* Radial (length-wise along spoke) text rendering */}
                        {item.imageUrl ? (
                          <g transform={`rotate(${centerAngle - 90} 100 100)`}>
                            <circle cx="176" cy="100" r="9.5" fill="white" stroke="white" strokeWidth="1" />
                            <image
                              href={item.imageUrl}
                              x="167.5"
                              y="91.5"
                              width="17"
                              height="17"
                              clipPath={`url(#clip-${item.id}-${i})`}
                              preserveAspectRatio="xMidYMid slice"
                            />
                            <text
                              x="162"
                              y="100"
                              fill="white"
                              fontSize={items.length > 12 ? 4 : 5.2}
                              fontWeight="700"
                              textAnchor="end"
                              dominantBaseline="middle"
                            >
                              {item.label.length > 15 ? `${item.label.slice(0, 14)}…` : item.label}
                            </text>
                          </g>
                        ) : (
                          <g transform={`rotate(${centerAngle - 90} 100 100)`}>
                            <text
                              x="190"
                              y="100"
                              fill="white"
                              fontSize={
                                items.length > 30
                                  ? 3
                                  : items.length > 20
                                  ? 3.8
                                  : items.length > 10
                                  ? 4.8
                                  : 5.8
                              }
                              fontWeight="700"
                              textAnchor="end"
                              dominantBaseline="middle"
                            >
                              {item.label.length > 22 ? `${item.label.slice(0, 21)}…` : item.label}
                            </text>
                          </g>
                        )}
                      </g>
                    );
                  })
                )}

                {/* Outer Ring & Center Pin */}
                <circle cx="100" cy="100" r="98" fill="none" stroke="white" strokeWidth="3" opacity="0.9" />
                <circle cx="100" cy="100" r="16" fill="white" stroke="oklch(0.90 0.01 220)" strokeWidth="2" />
                <circle cx="100" cy="100" r="7" fill="oklch(0.58 0.18 240)" />
              </svg>
            </div>
          </div>

          {/* Result & Spin Button */}
          <div className="flex flex-col items-center gap-3 w-full">
            <p className="font-display text-2xl font-bold text-foreground min-h-8 text-center">
              {winnerItem ? (
                <span className="animate-reveal text-primary font-extrabold cursor-pointer" onClick={() => setShowWinnerModal(true)}>
                  🎉 Winner: {winnerItem.label}
                </span>
              ) : spinning ? (
                <span className="animate-tick-flash text-muted-foreground">Spinning…</span>
              ) : (
                "Tap Spin to Start"
              )}
            </p>

            {items.length < 2 ? (
              <EmptyHint>Add at least 2 entries to spin.</EmptyHint>
            ) : (
              <Button
                size="lg"
                className="h-14 px-12 text-lg font-bold"
                disabled={spinning}
                onClick={spin}
              >
                <RotateCcw className={`size-5 mr-2 ${spinning ? "animate-spin" : ""}`} />
                {spinning ? "Spinning…" : "Spin Wheel"}
              </Button>
            )}

            {winnerItem && (
              <div className="flex flex-wrap justify-center gap-2">
                <CopyButton text={winnerItem.label} label="Copy Winner" />
                <BrandedExportActions
                  title="Wheel Winner"
                  content={winnerItem.label}
                  filename="picamini-wheel-winner.png"
                />
              </div>
            )}
          </div>
        </div>

        {/* History Panel */}
        <HistoryPanel items={history} onClear={() => setHistory([])} title="Winners History" />
      </div>

      {/* 🎉 WINNER POPUP MODAL */}
      <Dialog open={showWinnerModal} onOpenChange={setShowWinnerModal}>
        <DialogContent className="w-[90vw] max-w-sm sm:max-w-md p-6 sm:p-7 rounded-2xl sm:rounded-3xl border border-border bg-card shadow-2xl text-center overflow-hidden">
          <DialogHeader className="flex flex-col items-center justify-center text-center space-y-2">
            <div className="flex size-14 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-500 border border-amber-500/20">
              <Trophy className="size-7" />
            </div>
            <DialogTitle className="font-display text-2xl font-bold tracking-tight text-foreground">
              We Have a Winner!
            </DialogTitle>
          </DialogHeader>

          {/* Winner Display Box */}
          <div className="my-4 w-full rounded-2xl border border-primary/20 bg-primary/10 p-5 shadow-sm flex flex-col items-center">
            {winnerItem?.imageUrl && (
              <img
                src={winnerItem.imageUrl}
                alt={winnerItem.label}
                className="size-24 sm:size-28 rounded-full object-cover border-4 border-amber-400 shadow-lift mb-3"
              />
            )}
            <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
              Winner Picked
            </p>
            <p className="font-display text-3xl font-extrabold text-primary break-words">
              {winnerItem?.label}
            </p>
          </div>

          {/* Actions Stack */}
          <div className="flex flex-col gap-2.5 w-full pt-1">
            <Button
              type="button"
              size="lg"
              className="w-full font-bold h-12 text-base"
              onClick={() => {
                setShowWinnerModal(false);
                spin();
              }}
            >
              <RotateCcw className="size-4 mr-2" /> Spin Again
            </Button>

            <div className="grid grid-cols-2 gap-2 w-full">
              <Button
                type="button"
                variant="outline"
                size="default"
                className="w-full font-semibold text-xs sm:text-sm"
                onClick={async () => {
                  if (winnerItem) {
                    await copyText(winnerItem.label);
                    toast.success("Copied to clipboard");
                  }
                }}
              >
                <Copy className="size-3.5 mr-1.5" /> Copy Name
              </Button>

              <Button
                type="button"
                variant="destructive"
                size="default"
                className="w-full font-semibold text-xs sm:text-sm"
                onClick={removeCurrentWinner}
              >
                <Trash2 className="size-3.5 mr-1.5" /> Remove
              </Button>
            </div>
            {winnerItem && (
              <BrandedExportActions
                title="Wheel Winner"
                content={winnerItem.label}
                filename="picamini-wheel-winner.png"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
